/**
 * New difficulty of a song (W2-10 projects-data; DESIGN.md §4.1, §5.16): the new chart shares the song and the
 * illustration (content-addressed, no copy) and copies tempo, offset and song details; it starts empty or as a copy
 * of this chart. The dialog picks the difficulty label (EZ, HD, IN, AT, SP or a custom one) and the level number,
 * shows the result ("HD Lv.11") and which difficulties the song already has, then creates and opens the chart.
 */

import { useId, useMemo, useState } from 'react';
import type { AppServices } from '../../app/registry/types';
import type { ProjectSummary } from '../../persist/types';
import { Button, Dialog, Segmented } from '../../ui';
import { useText } from './hooks';
import { m } from './library.messages';
import { createDifficulty, displayName } from './libraryActions';
import {
  joinList,
  LEVEL_LABELS,
  LEVEL_MAX,
  LEVEL_MIN,
  levelString,
  nextLevelLabel,
  parseLevel,
  parseLevelNumber,
  songKey,
} from './libraryModel';
import s from './Library.module.css';

type LabelChoice = (typeof LEVEL_LABELS)[number] | 'other';

/** Usual level numbers per label, for a sensible default when the source has none. */
const TYPICAL_LEVEL: Readonly<Record<(typeof LEVEL_LABELS)[number], number>> = {
  EZ: 4,
  HD: 9,
  IN: 13,
  AT: 15,
  SP: 12,
};

export interface NewDifficultyDialogProps {
  readonly services: AppServices;
  readonly source: ProjectSummary;
  /** Every live chart (the song's other difficulties are listed and their labels skipped by default). */
  readonly projects: readonly ProjectSummary[];
  readonly onClose: () => void;
}

export function NewDifficultyDialog(props: NewDifficultyDialogProps) {
  const { services, source, onClose } = props;
  const text = useText(services);
  const ids = useId();
  const siblings = useMemo(
    () => props.projects.filter((p) => p.trashedAt === null && songKey(p) === songKey(source)),
    [props.projects, source],
  );
  const initialLabel = nextLevelLabel(siblings.map((p) => p.level));
  const [start, setStart] = useState<'empty' | 'copy'>('empty');
  const [label, setLabel] = useState<LabelChoice>(initialLabel as LabelChoice);
  const [custom, setCustom] = useState('');
  const [difficulty, setDifficulty] = useState<string>(() =>
    String(TYPICAL_LEVEL[initialLabel as (typeof LEVEL_LABELS)[number]]),
  );
  const [touched, setTouched] = useState(false);
  const [creating, setCreating] = useState(false);

  const labelText = label === 'other' ? custom.trim() : label;
  // 0 to 20 in steps of 0.1, like the New chart wizard and Phira's own field (DESIGN.md §5.1).
  const number = parseLevelNumber(difficulty);
  const numberOk = number !== null;
  const level = levelString(labelText, number ?? 0);
  const existing = siblings.map((p) => (p.level.trim() === '' ? text.t(m.noLevel) : p.level.trim()));
  const name = displayName(services, source);
  const taken = siblings.some(
    (p) => parseLevel(p.level).label.toUpperCase() === labelText.toUpperCase() && labelText !== '',
  );

  const create = async (): Promise<void> => {
    if (number === null || creating) return;
    setCreating(true);
    const id = await createDifficulty(services, source, { start, level, difficulty: number });
    if (id === null) setCreating(false);
    else onClose();
  };

  const reason = creating
    ? { key: m.ndCreating }
    : !numberOk
      ? { key: m.ndNumberInvalid, params: { min: LEVEL_MIN, max: LEVEL_MAX } }
      : null;

  return (
    <Dialog
      open
      size="md"
      title={text.t(m.ndTitle, { name })}
      description={text.t(m.ndDescription)}
      onClose={() => {
        if (!creating) onClose();
      }}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabledReason={creating ? { key: m.ndCreating } : null}>
            {text.t(m.ndCancel)}
          </Button>
          <Button
            variant="primary"
            icon="CopyPlus"
            type="submit"
            disabledReason={reason}
            onClick={() => {
              void create();
            }}
          >
            {text.t(creating ? m.ndCreating : m.ndCreate)}
          </Button>
        </>
      }
    >
      <form
        className={s.form}
        onSubmit={(e) => {
          e.preventDefault();
          void create();
        }}
      >
        <fieldset className={s.fieldset}>
          <legend className={s.legend}>{text.t(m.ndStart)}</legend>
          {(['empty', 'copy'] as const).map((value) => (
            <label key={value} className={s.radioCard} data-checked={start === value || undefined}>
              <input
                type="radio"
                name={`${ids}-start`}
                value={value}
                checked={start === value}
                onChange={() => {
                  setStart(value);
                }}
              />
              <span>
                <strong>{text.t(value === 'empty' ? m.ndEmpty : m.ndCopy)}</strong>
                <span className={s.muted}>{text.t(value === 'empty' ? m.ndEmptyHelp : m.ndCopyHelp)}</span>
              </span>
            </label>
          ))}
        </fieldset>

        <Segmented<LabelChoice>
          label={text.t(m.ndLabel)}
          value={label}
          options={[
            ...LEVEL_LABELS.map((l) => ({ value: l, label: l })),
            { value: 'other' as const, label: text.t(m.ndLabelOther) },
          ]}
          onChange={(v) => {
            setLabel(v);
            if (v !== 'other' && !touched) setDifficulty(String(TYPICAL_LEVEL[v]));
          }}
        />
        {label === 'other' && (
          <label className={s.inputRow}>
            <span>{text.t(m.ndCustomLabel)}</span>
            <input
              className={s.input}
              value={custom}
              maxLength={24}
              onChange={(e) => {
                setCustom(e.target.value);
              }}
            />
          </label>
        )}
        <label className={s.inputRow}>
          <span>{text.t(m.ndNumber)}</span>
          <input
            className={s.input}
            type="number"
            inputMode="decimal"
            min={LEVEL_MIN}
            max={LEVEL_MAX}
            step={0.1}
            value={difficulty}
            aria-invalid={!numberOk || undefined}
            aria-describedby={numberOk ? undefined : `${ids}-number-error`}
            onChange={(e) => {
              setTouched(true);
              setDifficulty(e.target.value);
            }}
          />
        </label>
        {!numberOk && (
          <p id={`${ids}-number-error`} className={s.warningText} role="status">
            {text.t(m.ndNumberInvalid, { min: LEVEL_MIN, max: LEVEL_MAX })}
          </p>
        )}
        <p className={s.preview} aria-live="polite">
          {text.t(m.ndPreview, { level })}
        </p>
        {existing.length > 0 && (
          <p className={s.muted}>{text.t(m.ndExisting, { levels: joinList(text.locale, existing) })}</p>
        )}
        {taken && (
          <p className={s.warningText} role="status">
            {text.t(m.ndLabelTaken, { label: labelText })}
          </p>
        )}
      </form>
    </Dialog>
  );
}
